import type * as GraphQLTypes from "src/graphql/types";

/**
 * Extract operation names from generated GraphQL types.
 * From exports like "ListFoldersDocument" we extract "ListFolders".
 */
type ExtractOperationName<K> = K extends `${infer Name}Document` ? Name : never;

/**
 * Union of all valid GraphQL operation names.
 * Derived from the generated types.ts exports.
 */
export type OperationName = ExtractOperationName<keyof typeof GraphQLTypes>;

/**
 * Helper to create a type-checked set of operation names.
 * TypeScript will error on typos.
 */
function createOperationSet<const T extends readonly OperationName[]>(
  operations: T,
): Set<OperationName> {
  return new Set(operations);
}

/**
 * Operations that can safely route to the fast API (us-east-1).
 *
 * Adding an operation here means it will automatically route to us-east-1
 * regardless of workspace region. Forgetting to add an operation here just
 * means it stays regional (slower, but safe). Removing an entry is also
 * safe — the op falls back to regional routing.
 *
 * ─── Auditing a candidate ───
 *
 * Walk every selected field in the GraphQL operation, including every
 * field inside every spread fragment. One safe field doesn't make the
 * operation safe — a single bucket-B or bucket-C field anywhere in the
 * selection disqualifies the whole op.
 *
 *   A. Hasura-native column or regular FK relationship — safe. The control
 *      plane Postgres is global in us-east-1.
 *   B. Hasura remote_relationship that resolves into a backend resolver
 *      which calls `getSource()` / `getDestination()` and uses the returned
 *      instance (e.g. its `sqlTransformer`, columns lookup, or methods).
 *      UNSAFE. Tunneled / region-bound resources may not be reachable
 *      from us-east-1. Example: `segment.syncable_columns` resolves via
 *      `listSyncableColumns`, which calls `getSource()` with default
 *      `excludeTunnel: false` and hits Passage via `getConnectionDetails`
 *      for tunneled sources.
 *   C. Field whose resolver can invoke destination-implementer code that
 *      reaches out to a third-party (customer's warehouse, Salesforce,
 *      Customer.io, Mailchimp, Bing Ads, etc.). UNSAFE. Two paths today:
 *        - `applyMigrations` via `getTransformedSync*Configuration`
 *          (triggered by `transformed_config` selections). Migration
 *          bodies are destination-defined; several do outbound I/O in
 *          their migrations.ts files. Any new destination can add one
 *          silently.
 *        - Destination instance methods on a constructed destination
 *          (e.g. `formatMetadata` via `getSyncMetadata` for
 *          `syncs.labels`).
 *      The rule is invariant to how rare or graceful the failure is in
 *      production today — once the code path can reach out, future
 *      destinations / migrations can change the failure profile without
 *      anyone re-auditing this list.
 *   D. Static catalog / control-plane SQL — safe.
 *
 * ─── Verified-safe resolver patterns ───
 *
 *   - `getSourceDefinitionV2`, `getDestinationDefinition` — return static
 *     source/destination catalog from Sanity + bundled JS. No I/O against
 *     the customer's source or destination.
 *   - `getSourceStrippedConfiguration` — registered as
 *     `requireSource(..., { excludeTunnel: true, cleanRoomSafe: true })`.
 *     `excludeTunnel` skips the `getConnectionDetails` / Passage call.
 *     Receives the source config from the global control plane, deletes
 *     secret fields, returns. Safe in isolation, but doesn't make a query
 *     safe if it also selects bucket B or C fields.
 *   - `listFilterableColumns`, `audienceReferenceStatsResolver`,
 *     `sampledModelAvailable`, `listAllTraitReferencesV2` — pure
 *     pgClient SQL against control-plane tables.
 *   - `getSchemaGraph` — registered as
 *     `requireSource(..., { excludeTunnel: true })`. The resolver only
 *     reads `source.id` and runs Hasura SELECTs against `segments` /
 *     `model_relationships`; no destination instance construction, no
 *     `applyMigrations`, no tunnel I/O.
 *
 * ─── Known-unsafe resolver patterns ───
 *
 *   - `getSyncMetadata` (resolver behind `syncs.labels`) — runs
 *     `applyMigrations`, then constructs a destination instance via
 *     `getDestination(...)` and calls `formatMetadata` when the
 *     destination uses formkit. Both steps are region-dependent. The
 *     try/catch returns `{}` on error, so the failure mode is silent
 *     label disappearance.
 *   - `getTransformedSyncConfiguration`,
 *     `getTransformedSyncTemplateConfiguration`,
 *     `getTransformedRetlAndRealtimeSyncConfiguration` — call
 *     `applyMigrations`. The migration bodies are destination code.
 *   - `listSyncableColumns` — calls `getSource()` without `excludeTunnel`,
 *     then dereferences `source.definition.sqlTransformer`. Region-bound
 *     for tunneled sources.
 *   - Any future resolver that constructs a source/destination instance
 *     and calls an instance method, or runs `applyMigrations`.
 *
 * Payload size is a real frontend concern but not a region-safety reason;
 * keep that analysis separate from this list.
 *
 * If you're not sure, leave it out. The cost is one regional round-trip;
 * the cost of a wrong promotion is silent feature degradation for every
 * non-us-east-1 workspace, often only visible when a single customer with
 * a tunneled source or a destination with non-trivial migrations hits it.
 *
 * See `fast-operations.test.ts` for the registry of operations the audit
 * has explicitly rejected, with the specific field selection or resolver
 * that disqualified each. The test suite asserts those names are absent
 * from this set, so once an op is in that list it can't be quietly
 * re-promoted without removing the test entry first.
 */
export const FAST_OPERATIONS = createOperationSet([
  // ─── User & Workspace Context ───
  "Initial",
  "Workspaces",
  "SearchWorkspaces",

  // ─── Permissions & Authorization ───
  "PermissionsV2",
  "PermissionsBatchV2",
  "OrganizationPermissions",
  "SendOrganizationInvite",
  "SchemaObjectCreationAuthorization",
  "SyncCreationAuthorization",
  "ModelCreationAuthorization",
  "AudienceCreationAuthorization",
  "ChartConfigurationAuthorization",

  // ─── Entitlements ───
  "GetWorkspaceEntitlements",

  // ─── Folders (queries and mutations) ───
  "ListFolders",
  "CreateFolder",
  "UpdateFolders",
  "DeleteFolder",

  // ─── Audiences ───
  // visualQuerySQL builds preview SQL from control-plane metadata only. The
  // source lookup uses excludeTunnel and must not execute customer-warehouse I/O.
  "VisualQuerySql",
  // RunVisualBackground / RunSqlBackground resolvers enqueue only (compile or
  // auth + SQS send on API). Queue name is async_previews-${workspace.region}.fifo
  // on backend; worker runs in that region. requireSource uses excludeTunnel.
  // No customer-warehouse execution on API.
  "RunVisualBackground",
  "RunSqlBackground",
  "Audiences",
  "AudienceParent",
  "AudienceExploreDependencies",
  "AudienceFilterOptions",
  "AudienceSummary",
  "CachedAudienceSizes",
  "ReadOnlyAudienceDependencies",
  "RelatedAudiencesSearch",
  "AudienceMembershipsMetadata",

  // ─── Models ───
  "AudienceSchemaGraph",
  "ModelColumns",
  "ParentModelSelect",
  "ParentModelsAndDestinations",
  "ParentModelAndRelationships",
  "ParentModelForQueryBuilder",
  "ParentModelsForAnalytics",
  "SchemaModelsForTraits",
  "AllFilterableColumns",
  "FilterableColumns",
  "FilterableAudienceColumnsForSegmentIds",

  // ─── Traits ───
  "Trait",
  "TraitsForParentModel",
  "ParentModelForTrait",

  // ─── Workspace Resources ───
  "LinkableWorkspaceResources",
  "Views",
  "Drafts",
  "RecentlyViewed",

  // ─── dbt ───
  "DbtSyncConfigs",

  // ─── Syncs (config and metadata) ───
  "Syncs",
  "SyncsByFolders",
  "SyncFilterOptions",
  "SyncTemplatesForParentModel",
  "TargetSyncRequests",
  "SyncHealth",
  "SyncRuns",
  "SyncsForSequences",
  "SyncsMonitorStatuses",

  // ─── Sync Triggers (write to DB, regional workers poll their queue) ───
  "StartSyncRun",
  "CancelSyncRun",

  // ─── Identity Resolution Metadata ───
  "IdentityResolutionGraph",

  // ─── Storage ───
  "ExternalBuckets",

  // ─── Journeys ───
  "QueryBuilderJourneys",

  // ─── Dashboards & Analytics Metadata ───
  "AudiencesAndMetricsForParentModel",
  "Dashboards",

  // ─── Activity Feed ───
  "HomeActivityFeed",

  // ─── Audit Log ───
  "Changelog",
  "ChangelogList",

  // ─── Decision Engines ───
  "DecisionEngine",
  "DecisionEngineFlow",
  "DecisionEngineFlowTag",
  "EligibleTargetAudiences",

  // ─── Agent Usage Credits ───
  "AgentUsageCredits",

  // ─── Agents Page Metadata ───
  "SelectableParentModels",
  "ConnectedChannels",
  "DestinationDefinitions",
]);

/**
 * Operations that must NEVER route to the fast API, even when a call site
 * passes `meta: { isFastEndpoint: true }`. A member is region-unsafe when
 * it selects a field whose resolver runs destination or source code that
 * may be unreachable from us-east-1 (`transformed_config` → `applyMigrations`,
 * `syncable_columns`, `syncs.labels` → `formatMetadata`), or when it writes
 * the agent harness event stream or job queue of the workspace region
 * (`CreateAmpAgent`, `SendAmpAgentMessage`, `StopAmpAgent`).
 *
 * This is the runtime guard that closes the `isFastEndpoint` override hole:
 * the allowlist only governs registry-based routing, so without this denylist
 * a call site could force an unsafe op fast. `fetcher.ts` gives this set
 * precedence over the meta override.
 *
 * Audit reasoning per entry lives in `fast-operations.test.ts` (kept out of
 * the browser bundle). The test asserts parity between that reason map and
 * this set.
 */
const UNSAFE_OPERATION_NAMES = [
  "CreateAmpAgent",
  "SendAmpAgentMessage",
  "StopAmpAgent",
  "ModelWithoutColumns",
  "Sync",
  "SyncConfiguration",
  "SyncsLabels",
  "SyncTemplate",
  "SyncTemplateDefinitions",
] as const satisfies readonly OperationName[];

/**
 * Union of the denylisted operation names. The reason map in
 * `fast-operations.test.ts` is typed `Record<UnsafeOperationName, string>`, so
 * adding an entry here without a documented reason (or leaving a stale reason)
 * is a compile-time error — not just a test failure.
 */
export type UnsafeOperationName = (typeof UNSAFE_OPERATION_NAMES)[number];

export const UNSAFE_OPERATIONS: ReadonlySet<OperationName> = new Set(
  UNSAFE_OPERATION_NAMES,
);
